import {useState} from 'react'
import {Box, Button, Chip, CircularProgress, Paper, Typography} from '@mui/material'
import {
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
} from '@mui/icons-material'
import type {AppArtifactBuilderData} from './types'

interface AppArtifactBuilderCardProps {
  data: AppArtifactBuilderData
  onDownload?: (data: AppArtifactBuilderData) => Promise<void>
}

export default function AppArtifactBuilderCard({data, onDownload}: AppArtifactBuilderCardProps) {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const download = async () => {
    if (!onDownload || downloading) return
    setDownloading(true)
    setError(null)
    try {
      await onDownload(data)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The skill could not be downloaded.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5,
        overflow: 'hidden',
        borderRadius: 3,
        borderColor: 'rgba(0,143,168,0.3)',
        background: theme =>
          `linear-gradient(135deg, ${theme.palette.background.paper} 0%, rgba(0,143,168,0.055) 100%)`,
      }}
    >
      <Box sx={{display: 'flex', gap: 1.5, p: 2}}>
        <Box sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: 'rgba(0,143,168,0.12)',
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <AutoAwesomeOutlinedIcon />
        </Box>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
            <Typography sx={{fontWeight: 700}}>{data.name}</Typography>
            <Chip size="small" variant="outlined" label={`v${data.version}`} />
            <Chip size="small" variant="outlined" color="primary" label="Skill" />
          </Box>
          <Typography sx={{mt: 0.6, color: 'text.secondary', fontSize: '0.82rem', lineHeight: 1.5}}>
            {data.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
          disabled={!onDownload || downloading}
          onClick={() => { void download() }}
          sx={{alignSelf: 'flex-start', whiteSpace: 'nowrap'}}
        >
          {downloading ? 'Downloading…' : 'Download skill'}
        </Button>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
        gap: 1,
        px: 2,
        pb: 2,
      }}>
        {data.instructions.map(instruction => (
          <Box key={instruction.client} sx={{p: 1.1, borderRadius: 2, bgcolor: 'action.hover'}}>
            <Typography sx={{fontSize: '0.72rem', fontWeight: 700}}>{instruction.client}</Typography>
            <Typography sx={{fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.4, mt: 0.2}}>
              {instruction.text}
            </Typography>
          </Box>
        ))}
      </Box>

      {error && (
        <Typography role="alert" sx={{px: 2, pb: 1.5, color: 'error.main', fontSize: '0.75rem'}}>
          {error}
        </Typography>
      )}
    </Paper>
  )
}
